import React, { useEffect, useLayoutEffect, useState } from "react";
import { Box } from "@mui/material";
import styles from "./styles";
import withStyles from "@mui/styles/withStyles";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import CircularProgress from "@mui/material/CircularProgress";
import { useGetSecureDocument } from "hooks/useGetSecureDocument";
import { SubHeading } from "@answersnow/pkg-theme";
import actions from "actions";
import { useDispatch } from "react-redux";
import { DOCUMENTS_VIEW_SUPPORTED_FILES } from "@constants";
import DynamicPageError from "components/DynamicPageError";
import { useGetUserPermissions } from "hooks/api/useGetUserPermissions";
import { getDocumentPreviewUrl } from "api/customers";
import DocViewerBar from "./DocViewerBar";
import { downloadDocument } from "./downloadDocument";
import { DocumentRegistrationProvider } from "./DocumentRegistrationProvider";
import DocumentRenderer from "./DocumentRenderer";

const EXTENSION_TO_MIME: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  html: "text/html",
  htm: "text/html",
};

interface DocumentViewerProps {
  classes: {
    contentError: string;
    content: string;
  };
}

function DocumentViewerContent(props: DocumentViewerProps) {
  "use memo";
  const { classes } = props;
  const { userId, documentId } = useParams<{ userId: string; documentId: string }>();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const role = query.get("role");
  const documentType = query.get("documentType");
  const suffix = query.get("suffix");

  const dispatch = useDispatch();
  const [unsupportedFile, setUnsupportedFile] = useState(true);

  const {
    data: response,
    isLoading: isDocumentLoading,
    isError: isDocumentError,
  } = useGetSecureDocument({ documentId, role: Boolean(role), documentType, enabled: !suffix });

  const {
    data: documentBlob,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery(
    ["viewerDocumentBlob", suffix],
    async () => {
      const { url } = await getDocumentPreviewUrl(suffix);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch document");
      return res.blob();
    },
    {
      enabled: Boolean(suffix),
      staleTime: 5 * 60 * 1000,
      // Refetching creates a new blob URL and resets the reader's page.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const [documentBlobUrl, setDocumentBlobUrl] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (!documentBlob) {
      setDocumentBlobUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(documentBlob);
    setDocumentBlobUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [documentBlob]);

  const { userPermissions, permissionsLoading } = useGetUserPermissions(userId);

  const documentUri = suffix ? documentBlobUrl : response?.document;
  const filename = suffix ? decodeURIComponent(suffix).split("/").pop() : response?.filename;
  const isDocLoading = suffix ? isPreviewLoading : isDocumentLoading;
  const extension = suffix ? decodeURIComponent(suffix).split(".").pop()?.toLowerCase() : undefined;
  const contentType = suffix ? extension && EXTENSION_TO_MIME[extension] : response?.contentType;

  useEffect(() => {
    dispatch(actions.checkAuthState());
    dispatch(actions.setPageDetails({ pageName: "Document" }));
  }, []);

  useEffect(() => {
    if (suffix) {
      setUnsupportedFile(!DOCUMENTS_VIEW_SUPPORTED_FILES.includes(contentType));
      return;
    }
    if (userPermissions && !isDocumentLoading && !isDocumentError) {
      if (DOCUMENTS_VIEW_SUPPORTED_FILES.includes(contentType)) {
        setUnsupportedFile(false);
      } else {
        if (userPermissions?.download_client_documents) {
          downloadDocument(documentUri, filename);
          window.close();
        }
      }
    }
  }, [userPermissions, isDocumentLoading, isDocumentError, suffix, contentType]);

  const isError = suffix ? isPreviewError : isDocumentError;
  const toolbar = (
    <DocViewerBar
      isError={isError}
      filename={filename}
      documentUri={documentUri}
      unsupportedFile={unsupportedFile}
      userPermissions={userPermissions}
    />
  );

  if (permissionsLoading || isDocLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" gap={2}>
        <CircularProgress size={30} color="primary" />
        <SubHeading>Loading</SubHeading>
      </Box>
    );
  }

  if (isError || unsupportedFile) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={2}
        className={classes.contentError}
      >
        {toolbar}
        <DynamicPageError
          title="Document"
          message="Sorry, this file can't be viewed. Please contact customer support for help."
        />
      </Box>
    );
  }
  return (
    <Box display="flex" justifyContent="center" className={classes.content}>
      <DocumentRenderer documentUri={documentUri} toolbar={toolbar} />
    </Box>
  );
}

function DocumentViewer(props: DocumentViewerProps) {
  return (
    <DocumentRegistrationProvider>
      <DocumentViewerContent {...props} />
    </DocumentRegistrationProvider>
  );
}

export default withStyles(styles)(DocumentViewer);
